// ============================================================================
// SCHEDULING AVAILABILITY — booking-constraint source of truth
//
// Doc: tenants/{tenantId}/settings/schedulingAvailability
//   {
//     timezone: string,        // e.g. "America/New_York"
//     workingDays: number[],   // 0=Sun .. 6=Sat
//     startTime: string,       // "HH:MM" 24h (inclusive)
//     endTime:   string,       // "HH:MM" 24h (EXCLUSIVE)
//     schemaVersion: 1,
//     updatedAt: timestamp,
//     updatedByUid: string
//   }
//
// This governs APPOINTMENT BOOKING only. It is independent from
// settings/impact.businessHours, which governs SLA elapsed-time measurement.
// There is NO hidden fallback to SLA business hours.
//
// Weekday eligibility is computed from the calendar date itself (UTC parts),
// so it never shifts with the viewer's browser timezone.
// ============================================================================

import { getApp } from 'firebase/app';
import { getFirestore, doc, getDoc, setDoc, serverTimestamp } from 'firebase/firestore';
import { PATHS } from './firestorePaths.js';

export const SCHEDULING_AVAILABILITY_SCHEMA_VERSION = 1;

function getDb() {
  return getFirestore(getApp());
}

// ── Pure helpers (UI + store share these) ────────────────────────────────────

/**
 * Weekday index (0=Sun .. 6=Sat) for a 'YYYY-MM-DD' calendar date.
 * Uses UTC parts so the result is independent of the browser timezone —
 * the calendar day's weekday is fixed regardless of where it is viewed.
 * @returns {number|null}
 */
export function weekdayIndexForDate(dateStr) {
  if (typeof dateStr !== 'string') return null;
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(dateStr);
  if (!m) return null;
  const y = Number(m[1]); const mo = Number(m[2]); const d = Number(m[3]);
  if (!y || !mo || !d) return null;
  return new Date(Date.UTC(y, mo - 1, d)).getUTCDay();
}

/** True when the date's weekday is in availability.workingDays. */
export function isDayAllowed(availability, dateStr) {
  if (!availability || !Array.isArray(availability.workingDays)) return false;
  const wd = weekdayIndexForDate(dateStr);
  if (wd === null) return false;
  return availability.workingDays.includes(wd);
}

/** True when startTime <= time24 < endTime (endTime EXCLUSIVE). time24 = "HH:MM". */
export function isTimeAllowed(availability, time24) {
  if (!availability || typeof time24 !== 'string') return false;
  const { startTime, endTime } = availability;
  if (typeof startTime !== 'string' || typeof endTime !== 'string') return false;
  return time24 >= startTime && time24 < endTime;
}

/** True when both the day and the time are allowed. */
export function isSlotAllowed(availability, dateStr, time24) {
  return isDayAllowed(availability, dateStr) && isTimeAllowed(availability, time24);
}

// ── Validation + builder ──────────────────────────────────────────────────────

const TIME_RE = /^([01]\d|2[0-3]):[0-5]\d$/;

export function validateSchedulingAvailability({ timezone, workingDays, startTime, endTime }) {
  const errors = [];
  if (!timezone || typeof timezone !== 'string') errors.push('Timezone is required.');
  if (!Array.isArray(workingDays) || workingDays.length === 0) {
    errors.push('Select at least one working day.');
  } else if (workingDays.some((d) => !Number.isInteger(d) || d < 0 || d > 6)) {
    errors.push('Working days must be integers 0–6 (Sun–Sat).');
  }
  if (!TIME_RE.test(startTime || '')) errors.push('Start time must be HH:MM (24h).');
  if (!TIME_RE.test(endTime || '')) errors.push('End time must be HH:MM (24h).');
  if (TIME_RE.test(startTime || '') && TIME_RE.test(endTime || '') && !(startTime < endTime)) {
    errors.push('Start time must be earlier than end time.');
  }
  return { valid: errors.length === 0, errors };
}

export function buildSchedulingAvailabilityDoc({ timezone, workingDays, startTime, endTime, updatedByUid }) {
  const { valid, errors } = validateSchedulingAvailability({ timezone, workingDays, startTime, endTime });
  if (!valid) throw new Error(`Invalid scheduling availability: ${errors.join(' ')}`);
  if (!updatedByUid) throw new Error('buildSchedulingAvailabilityDoc: updatedByUid is required');
  return {
    timezone,
    workingDays: [...workingDays].sort((a, b) => a - b),
    startTime,
    endTime,
    schemaVersion: SCHEDULING_AVAILABILITY_SCHEMA_VERSION,
    updatedAt: serverTimestamp(),
    updatedByUid,
  };
}

// ── Reads / writes ────────────────────────────────────────────────────────────

/**
 * Read scheduling availability. Distinguishes "missing doc" from "read error":
 *   - resolves { exists: false, availability: null } when the doc is absent,
 *   - resolves { exists: true, availability } when present,
 *   - REJECTS (throws) on permission/network failure so callers can fail fast.
 */
export async function getSchedulingAvailability(tenantId) {
  if (!tenantId) throw new Error('getSchedulingAvailability: tenantId is required');
  const snap = await getDoc(doc(getDb(), PATHS.schedulingAvailability(tenantId)));
  if (!snap.exists()) return { exists: false, availability: null };
  return { exists: true, availability: snap.data() };
}

/** Write scheduling availability (tenant_admin / platform_admin only, enforced by rules). */
export async function saveSchedulingAvailability(tenantId, params) {
  if (!tenantId) throw new Error('saveSchedulingAvailability: tenantId is required');
  const docData = buildSchedulingAvailabilityDoc(params);
  await setDoc(doc(getDb(), PATHS.schedulingAvailability(tenantId)), docData);
  return docData;
}
